import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { emailsAPI } from '@/utils/api';
import useAppStore from '@/store/appStore';

export function useEmails(filters = {}) {
  return useQuery({
    queryKey: ['emails', filters],
    queryFn: async () => {
      const response = await emailsAPI.getAll(filters);
      return response.data;
    },
  });
}

export function useEmail(id) {
  return useQuery({
    queryKey: ['emails', id],
    queryFn: async () => {
      const response = await emailsAPI.getById(id);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useEmailBody(id) {
  return useQuery({
    queryKey: ['emails', id, 'body'],
    queryFn: async () => {
      const response = await emailsAPI.getBody(id);
      return response.data;
    },
    enabled: !!id,
    staleTime: 60000, // Cache for 1 minute
  });
}

export function useEmailStats(accountId = null) {
  return useQuery({
    queryKey: ['emails', 'stats', accountId],
    queryFn: async () => {
      const response = await emailsAPI.getStats(accountId);
      return response.data;
    },
  });
}

export function useUpdateEmail() {
  const queryClient = useQueryClient();
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: ({ id, data }) => emailsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
      addNotification({
        type: 'success',
        title: 'Email Updated',
        message: 'Email updated successfully',
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Update Email',
        message: error.message,
      });
    },
  });
}

export function useDeleteEmail() {
  const queryClient = useQueryClient();
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: (id) => emailsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
      addNotification({
        type: 'success',
        title: 'Email Deleted',
        message: 'Email deleted successfully',
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Failed to Delete Email',
        message: error.message,
      });
    },
  });
}

export function useBulkUpdateEmails() {
  const queryClient = useQueryClient();
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: ({ emailIds, updates }) => emailsAPI.bulkUpdate(emailIds, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
      addNotification({
        type: 'success',
        title: 'Emails Updated',
        message: 'Bulk update completed successfully',
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Bulk Update Failed',
        message: error.message,
      });
    },
  });
}

export function useBulkDeleteEmails() {
  const queryClient = useQueryClient();
  const { addNotification } = useAppStore();

  return useMutation({
    mutationFn: (emailIds) => emailsAPI.bulkDelete(emailIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emails'] });
      addNotification({
        type: 'success',
        title: 'Emails Deleted',
        message: 'Bulk delete completed successfully',
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Bulk Delete Failed',
        message: error.message,
      });
    },
  });
}
